import { makeDriveFolderNode, type DriveFolderNode, type DriveFolderRecipe } from "./driveFolderRecipeModel.js";

function mapNodes(nodes: DriveFolderNode[], fn: (n: DriveFolderNode) => DriveFolderNode | null): DriveFolderNode[] {
  const out: DriveFolderNode[] = [];
  for (const n of nodes) {
    const next = fn(n);
    if (next) out.push(next);
  }
  return out;
}

export function collectDriveFolderSubtreeIds(node: DriveFolderNode): Set<string> {
  const ids = new Set<string>([node.id]);
  for (const c of node.children) for (const id of collectDriveFolderSubtreeIds(c)) ids.add(id);
  return ids;
}

export function findDriveFolderNode(nodes: DriveFolderNode[], id: string): DriveFolderNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    const inner = findDriveFolderNode(n.children, id);
    if (inner) return inner;
  }
  return null;
}

export function updateDriveFolderNode(
  nodes: DriveFolderNode[],
  id: string,
  patch: Partial<Pick<DriveFolderNode, "name" | "matchKey" | "matchValue">>
): DriveFolderNode[] {
  return nodes.map((n) => {
    if (n.id === id) return { ...n, ...patch };
    return { ...n, children: updateDriveFolderNode(n.children, id, patch) };
  });
}

export function removeDriveFolderNode(nodes: DriveFolderNode[], id: string): DriveFolderNode[] {
  return mapNodes(nodes, (n) => {
    if (n.id === id) return null;
    return { ...n, children: removeDriveFolderNode(n.children, id) };
  });
}

export function addDriveFolderChild(nodes: DriveFolderNode[], parentId: string | null, child: DriveFolderNode): DriveFolderNode[] {
  if (!parentId) return [...nodes, child];
  return nodes.map((n) => {
    if (n.id === parentId) return { ...n, children: [...n.children, child] };
    return { ...n, children: addDriveFolderChild(n.children, parentId, child) };
  });
}

function takeNode(nodes: DriveFolderNode[], id: string): { rest: DriveFolderNode[]; taken: DriveFolderNode | null } {
  const rest: DriveFolderNode[] = [];
  let taken: DriveFolderNode | null = null;
  for (const n of nodes) {
    if (n.id === id) {
      taken = n;
      continue;
    }
    if (taken) {
      rest.push(n);
      continue;
    }
    const inner = takeNode(n.children, id);
    if (inner.taken) {
      taken = inner.taken;
      rest.push({ ...n, children: inner.rest });
    } else rest.push(n);
  }
  return { rest, taken };
}

function insertAt(nodes: DriveFolderNode[], parentId: string | null, index: number, node: DriveFolderNode): DriveFolderNode[] {
  if (!parentId) {
    const next = [...nodes];
    next.splice(Math.max(0, Math.min(index, next.length)), 0, node);
    return next;
  }
  return nodes.map((n) => {
    if (n.id === parentId) {
      const children = [...n.children];
      children.splice(Math.max(0, Math.min(index, children.length)), 0, node);
      return { ...n, children };
    }
    return { ...n, children: insertAt(n.children, parentId, index, node) };
  });
}

/** Reparent `id` as a child of `parentId` (null = root) at `index`. No-op if dropped on itself or a descendant. */
export function moveDriveFolderNode(
  nodes: DriveFolderNode[],
  id: string,
  parentId: string | null,
  index: number
): DriveFolderNode[] {
  if (id === parentId) return nodes;
  const moving = findDriveFolderNode(nodes, id);
  if (!moving) return nodes;
  if (parentId && collectDriveFolderSubtreeIds(moving).has(parentId)) return nodes;
  const { rest, taken } = takeNode(nodes, id);
  if (!taken) return nodes;
  return insertAt(rest, parentId, index, taken);
}

export function addRootDriveFolder(recipe: DriveFolderRecipe, name = "New folder"): DriveFolderRecipe {
  return { ...recipe, nodes: [...recipe.nodes, makeDriveFolderNode(name, null)] };
}
